/**
 * Är AI tillgängligt — och får det användas?
 *
 * TVÅ SKILDA FRÅGOR som båda måste vara ja, och som inte får blandas ihop:
 *
 *   TILLGÄNGLIGT (`user.aiAvailable`, från servern) — har den här driftsättningen en
 *   AI-nyckel alls? Utan nyckel svarar varje AI-anrop 500. Då ska ytorna inte finnas:
 *   en knapp som ser användbar ut men aldrig gör något är sämre än ingen knapp.
 *
 *   SAMTYCKT (`aiEnabled` i localStorage) — har användaren själv sagt ja till AI, efter
 *   att ha läst den juridiska texten? Det är ett aktivt val och rörs inte av det här.
 *
 * Ordningen spelar roll. Saknas nyckeln ska användaren inte ens TILLFRÅGAS om samtycke,
 * för samtycket skulle inte leda till något. Därför gatar inställningen och
 * onboarding-rutan på enbart tillgängligheten, medan funktionsytorna kräver båda.
 *
 * Funktionaliteten ligger kvar i sin helhet. Den dagen nyckeln finns i miljön svarar
 * servern aiAvailable=true och allt dyker upp igen — ingen kod behöver röras.
 */
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '@/contexts/AppContext';

/** Driftsättningen har en AI-nyckel. Serverns svar, inte ett klientval. */
export const useAiAvailable = (): boolean => {
  const { user } = useContext(AppContext);
  return user?.aiAvailable === true;
};

/**
 * Användarens samtycke. Läses live via 'storage' eftersom AIToggle och
 * AiOnboardingDialog skriver flaggan och dispatchar eventet — samma mönster som
 * useNpfNudgePrefs och useEffectiveChildMode.
 */
export const useAiConsent = (): boolean => {
  const [consent, setConsent] = useState(() => localStorage.getItem('aiEnabled') === 'true');

  useEffect(() => {
    const onStorage = () => setConsent(localStorage.getItem('aiEnabled') === 'true');
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return consent;
};

/**
 * AI-funktioner ska visas: nyckel finns OCH användaren har sagt ja.
 *
 * Båda hookarna anropas ALLTID, var för sig, innan svaren vägs ihop. Skrivet som
 * `useAiAvailable() && useAiConsent()` kortsluter `&&` när den första är false, och då
 * hoppas den andra hooken över — antalet hook-anrop ändras mellan renderingar och React
 * kopplar ihop fel state. Det är samma regel som react-hooks/rules-of-hooks vaktar.
 */
export const useAiUsable = (): boolean => {
  const available = useAiAvailable();
  const consented = useAiConsent();
  return available && consented;
};
